import _ from 'lodash'
import { ConnectionType, pointers } from '@wix/thunderbolt-becky-types'
import type {
	Component,
	Structure,
	Data,
	ActionBehavior,
	ObsoleteBehaviorsList,
	ViewMode,
	BaseDataItem,
	UnresolvedRefArray,
	UnresolvedVariantRelation,
	ConnectionList,
	IConnectionItem,
	ControllerConnectionItem,
	RepeaterDataItem,
	Style,
	SlotsDataItem,
	UnresolvedVariablesList,
	SingleLayoutData,
} from '@wix/thunderbolt-becky-types'
import type {
	HiddenCompsGroupedByRefComponentMap,
	InflatedByRefStructure,
	RefData,
	ResolvedRef,
} from '@wix/thunderbolt-becky-root'
import { getDisplayedId, getFullId, getItemId } from '@wix/thunderbolt-commons'
import { buildWidgetUrl } from './resolveRemoteRefs'
import {
	DATA_QUERIES_TO_INFLATE,
	getEmptyDataMaps,
	getUniqueId,
	getMultipleRefCompsIdFromInflatedId,
	REF_DELIMITER,
	removeHash,
	getTemplateFromInflatedId,
} from './utils'
import { getRefCompIds } from './presetsUtils'
import { inflateRefArrayWithPresets, getQueries } from './presetsInflation'
import { mergeRefArrayOverrides } from './inflationUtils'

const DATA_MAPS = pointers.data.DATA_MAPS
const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES
const QUERY_TO_MAP_NAME = pointers.QUERY_TO_MAP_NAME
const isMobile = (viewMode: ViewMode) => viewMode === 'mobile'

const getUniqueDataQuery = (id: string, suffix: string, withMobileSuffix?: boolean) =>
	`${id}-${suffix}${withMobileSuffix ? '-mobile' : ''}`

const inflateDataQueries = (compStructure: Component, compUniqueId: string, pageData: Data, viewMode: ViewMode) =>
	_(DATA_QUERIES_TO_INFLATE)
		.mapKeys((x) => x)
		.mapValues((dataQueryName) => {
			const isMobileProperty = isMobile(viewMode) && dataQueryName === PROPERTY_TYPES.PROPERTY_QUERY
			return getUniqueDataQuery(compUniqueId, dataQueryName, isMobileProperty)
		})
		.value()

const inflateSingleComp = (
	compStructure: Component,
	originalCompId: string,
	refId: string,
	rootId: string,
	pageData: Data,
	viewMode: ViewMode,
	resolvedData: Data | RefData,
	pageCompId: string
) => {
	const compUniqueId = getUniqueId(refId, originalCompId)
	const isRootComp = originalCompId === rootId

	const inflatedPropQuery = getUniqueDataQuery(compUniqueId, PROPERTY_TYPES.PROPERTY_QUERY, isMobile(viewMode))

	const originalPropQuery = compStructure[PROPERTY_TYPES.PROPERTY_QUERY]
		? compStructure[PROPERTY_TYPES.PROPERTY_QUERY]
		: ''

	const propertyItemOverrides: Partial<{ ghost?: string } & BaseDataItem<string>> = pageData[DATA_MAPS.PROPERTIES][
		inflatedPropQuery
	]
		? pageData[DATA_MAPS.PROPERTIES][inflatedPropQuery]
		: {}

	let originalPropertyItem: Partial<{ ghost?: string } & BaseDataItem<string>> = {}
	if (originalPropQuery) {
		originalPropertyItem = resolvedData[DATA_MAPS.PROPERTIES][originalPropQuery]
			? resolvedData[DATA_MAPS.PROPERTIES][originalPropQuery]
			: {}
	}

	const propertyItemToUse = _.isEmpty(propertyItemOverrides) ? originalPropertyItem : propertyItemOverrides

	const isGhostCollapsed = propertyItemToUse.ghost === 'COLLAPSED'

	const inflatedSingleComp = {
		...compStructure,
		...inflateDataQueries(compStructure, compUniqueId, pageData, viewMode),
		templateId: originalCompId,
		id: compUniqueId,
		metaData: {
			...compStructure.metaData,
			pageId: pageCompId,
		},
		parent: isRootComp ? refId : getUniqueId(refId, compStructure.parent as string),
		components: compStructure.components
			? compStructure.components.map((childCompId) => getUniqueId(refId, childCompId))
			: undefined,
	}
	return { inflatedSingleComp, isGhostCollapsed }
}

const emptyData = getEmptyDataMaps()

const inflateTemplateStructure = (
	templateStructure: Structure,
	refContextId: string,
	widgetRootId: string,
	viewMode: ViewMode,
	pageCompId: string,
	systemStyles: Record<string, boolean>
) => {
	return _(templateStructure)
		.mapValues((templateComp, templateCompId) => {
			const inflatedComp = inflateSingleComp(
				templateComp,
				templateCompId,
				refContextId,
				widgetRootId,
				// @ts-ignore
				emptyData,
				viewMode,
				emptyData,
				pageCompId
			).inflatedSingleComp

			if (systemStyles[templateComp.styleId]) {
				inflatedComp.styleId = templateComp.styleId
			}

			return inflatedComp
		})
		.mapKeys('id')
		.value()
}

const mergeData = (
	original: Data,
	resolvedRefMapToMerge: { [key: string]: ResolvedRef },
	remoteRefMapToMerge: { [key: string]: any },
	masterPageData: Data
) => {
	const { connections_data: originalConnectionData, ...originalWithoutConnections } = original
	const originalConnectionsWithoutCopied = { ...originalConnectionData }

	const resolvedRefMapper = (resolvedRef: ResolvedRef) => {
		const data = resolvedRef?.data ?? {}
		const { connections_data: resolvedConnectionData } = data

		if (!resolvedConnectionData) {
			return data
		}

		const fixedConnectionsDataEntries = Object.entries(resolvedConnectionData).map(([id, connectionItem]) => {
			if (!connectionItem?.items || !originalConnectionData[id]?.items) {
				return [id, connectionItem]
			}

			delete originalConnectionsWithoutCopied[id]

			return [
				id,
				{
					...connectionItem,
					// Combine the two item lists instead of overriding with _.merge to avoid strange overrides
					// https://github.com/wix-private/bolt/blob/master/santa-renderer/src/aspects/ComponentsModelAspect/carmi/utils/templateRefsUtils.carmi.js#L35
					items: [...connectionItem.items, ...originalConnectionData[id]?.items],
				},
			]
		})

		return {
			...data,
			connections_data: Object.fromEntries(fixedConnectionsDataEntries),
		}
	}

	const resolvedRefs = _.merge({}, ...Object.values(resolvedRefMapToMerge).map(resolvedRefMapper))
	const remoteRefs = _.merge({}, ...Object.values(remoteRefMapToMerge).map(resolvedRefMapper))
	const connections = { connections_data: originalConnectionsWithoutCopied }

	// We don't merge overrides of type RefArray, we resolve them during the inflation.
	const originalWithoutConnectionsAndOverridesRefArrays = _.mapValues(
		originalWithoutConnections,
		(dataMap, dataMapName) =>
			_.omitBy(dataMap, (dataItem, dataItemId) => {
				if (dataItem.type !== 'RefArray') {
					return false
				}
				const refArrayInResolvedMap = resolvedRefs[dataMapName]?.[dataItemId] as UnresolvedRefArray
				if (!refArrayInResolvedMap) {
					return false
				}

				return refArrayInResolvedMap.values.some((itemId) => itemId.split(REF_DELIMITER).length !== 1)
			})
	)

	// shallow merge each data type
	return _.mapValues(getEmptyDataMaps(), (_emptyObj, mapName) => ({
		...(resolvedRefs[mapName] || {}),
		...(remoteRefs[mapName] || {}),
		// @ts-ignore
		...(originalWithoutConnectionsAndOverridesRefArrays[mapName] || {}),
		// @ts-ignore
		...(connections[mapName] || {}),
		// TODO: remove after #TB-4561 resolve
		...(masterPageData[mapName as keyof Data] || {}),
	}))
}

const fixDataAfterInflate = (pageData: Data, originalCompIdToInflatedCompIdMap: { [key: string]: string }) => {
	const fixBehaviourItem = (behaviourItem: ActionBehavior) => {
		if (behaviourItem.action && originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]) {
			behaviourItem.action.sourceId = originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]
		}

		if (behaviourItem.behavior && originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]) {
			behaviourItem.behavior.targetId = originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]
		}

		return behaviourItem
	}

	Object.keys(pageData.behaviors_data).forEach((behaviourId) => {
		const dataItem = pageData.behaviors_data[behaviourId] as ObsoleteBehaviorsList

		if (dataItem.type === 'ObsoleteBehaviorsList') {
			const itemsString = (dataItem as ObsoleteBehaviorsList).items
			try {
				const itemsList = JSON.parse(itemsString) as Array<ActionBehavior>
				dataItem.items = JSON.stringify(itemsList.map(fixBehaviourItem))
				pageData.behaviors_data[behaviourId] = dataItem
			} catch (e) {
				console.error('Cannot stringify ObsoleteBehaviorsList', e)
			}
		}
	})

	return pageData
}

const getHiddenCompsGroupedByRefComponent = (structure: InflatedByRefStructure, data: Data | RefData) => {
	const hiddenCompsGroupedByRefComponent: HiddenCompsGroupedByRefComponentMap = {}

	_(structure).forEach((comp, compId) => {
		const compUniqueId = getMultipleRefCompsIdFromInflatedId(compId)

		const propQuery = comp[PROPERTY_TYPES.PROPERTY_QUERY] || ''
		const propertyItem = data[DATA_MAPS.PROPERTIES][propQuery] || {}

		// @ts-ignore
		const isGhostCollapsed = propertyItem.ghost === 'COLLAPSED'

		if (isGhostCollapsed) {
			hiddenCompsGroupedByRefComponent[compUniqueId] = Object.assign(
				hiddenCompsGroupedByRefComponent[compUniqueId] || {},
				{ [compId]: comp }
			)
		}
	})

	return hiddenCompsGroupedByRefComponent
}

const getQueriesToCompIdMap = (comp: Component) =>
	_(comp)
		.pick(DATA_QUERIES_TO_INFLATE)
		.invert()
		.mapValues(() => comp.id)
		.value()

const getInflatedVarId = (widgetRootId: string, parentRefCompId: string, varId: string) =>
	`${parentRefCompId}${REF_DELIMITER}${widgetRootId}-${varId}`

const inflateVariableConnections = (layout: SingleLayoutData, widgetRootId: string, parentRefCompId: string) => {
	const variableConnections = layout.variableConnections!.map((connection) => {
		const variableId = getInflatedVarId(widgetRootId, parentRefCompId, connection.variableId)

		return {
			path: connection.path,
			variableId,
		}
	})
	return {
		...layout,
		variableConnections,
	}
}

const inflateVariables = (
	parentRefCompId: string,
	widgetRootId: string,
	varsList: UnresolvedVariablesList,
	dataMap: any,
	pageCompId: string
) => {
	const inflatedItems: any = []

	const inflatedVars = varsList.variables.map((_varId) => {
		const varId = removeHash(_varId)
		const unitSizeVar = { ...dataMap[varId] }
		const refArrayId = removeHash(unitSizeVar.value)

		const inflatedVarId = getInflatedVarId(widgetRootId, parentRefCompId, varId)
		const inflatedRefArrayId = getInflatedVarId(widgetRootId, parentRefCompId, refArrayId)

		unitSizeVar.id = inflatedVarId
		unitSizeVar.value = `#${inflatedRefArrayId}`
		_.set(unitSizeVar, ['metaData', 'pageId'], pageCompId)

		inflatedItems.push(unitSizeVar)

		const refArray = { ...dataMap[refArrayId] }
		refArray.id = inflatedRefArrayId
		_.set(refArray, ['metaData', 'pageId'], pageCompId)

		inflatedItems.push(refArray)

		return `#${inflatedVarId}`
	})

	return _.keyBy([{ ...varsList, variables: inflatedVars }, ...inflatedItems], (item) => item.id)
}

const inflateActionBehaviorsItemsCompIds = (
	actionBehaviorDataItem: ObsoleteBehaviorsList,
	originalToInflatedCompIdMap: Record<string, string>
): ObsoleteBehaviorsList => {
	if (actionBehaviorDataItem.type !== 'ObsoleteBehaviorsList') {
		return actionBehaviorDataItem
	}

	try {
		const { items: itemsString } = actionBehaviorDataItem
		const itemsList = JSON.parse(itemsString) as Array<ActionBehavior>
		const fixedItems = itemsList.map((actionBehaviorItem) => {
			if (
				actionBehaviorItem.action?.sourceId &&
				originalToInflatedCompIdMap[actionBehaviorItem.action.sourceId]
			) {
				actionBehaviorItem.action.sourceId = originalToInflatedCompIdMap[actionBehaviorItem.action.sourceId]
			}

			if (
				actionBehaviorItem.behavior?.targetId &&
				originalToInflatedCompIdMap[actionBehaviorItem.behavior.targetId]
			) {
				actionBehaviorItem.behavior.targetId = originalToInflatedCompIdMap[actionBehaviorItem.behavior.targetId]
			}

			return actionBehaviorItem
		})

		return {
			...actionBehaviorDataItem,
			items: JSON.stringify(fixedItems),
		}
	} catch (e) {
		console.error('Error parsing ObsoleteBehaviorsList', e)
	}

	return actionBehaviorDataItem
}

const isControllerConnectionItem = (connectionItem: IConnectionItem): connectionItem is ControllerConnectionItem =>
	connectionItem.type === ConnectionType.ConnectionItem

const inflatedControllerConnectionsControllerId = (
	{ items, ...connectionList }: ConnectionList,
	parentRefId: string,
	queryToCompIdMap: Record<string, string>
) => ({
	...connectionList,
	items: items.map((connectionItem) => {
		if (!isControllerConnectionItem(connectionItem)) {
			return connectionItem
		}

		const controllerCompId = queryToCompIdMap[connectionItem.controllerId]

		return {
			...connectionItem,
			controllerId: getUniqueId(parentRefId, `${controllerCompId}-dataQuery`),
		}
	}),
})

const resolveInternalRefStructure = (rootId: string, masterPageStructure: Structure, masterPageData: Data) => {
	const structure: InflatedByRefStructure = {}
	const queriesToCompId: Record<string, string> = {}
	const repeatedCompIdsToRepeaterId: Record<string, string> = {}
	const repeaterItems: Record<string, Array<string>> = {}

	const queue = [rootId]
	while (queue.length) {
		const compId = queue.shift()!
		const comp = {
			...masterPageStructure[compId],
			templateId: compId,
		}

		structure[compId] = comp
		if (comp.components) {
			queue.push(...comp.components)
		}

		const isRepeater = comp.componentType === 'Repeater'
		if (isRepeater) {
			const { items } = masterPageData.document_data[comp.dataQuery!.replace(/#/, '')] as RepeaterDataItem
			repeaterItems[compId] = items
		}

		const repeaterAncestor = repeatedCompIdsToRepeaterId[compId]
		const isRepeaterDescendant = !!repeaterAncestor
		if (isRepeater || isRepeaterDescendant) {
			_.forEach(comp.components, (repeatedChildId) => {
				repeatedCompIdsToRepeaterId[repeatedChildId] = repeaterAncestor || compId
			})
		}

		Object.keys(QUERY_TO_MAP_NAME).forEach((queryName) => {
			const queryId = comp[queryName as keyof Component]
			if (queryId) {
				queriesToCompId[removeHash(queryId)] = compId
			}

			if (isRepeaterDescendant && (queryName === 'dataQuery' || queryName === 'designQuery')) {
				const items = repeaterItems[repeaterAncestor]
				_.forEach(items, (itemId) => {
					queriesToCompId[`${removeHash(queryId)}__${itemId}`] = compId
				})
			}
		})
	}

	return {
		structure,
		queriesToCompId,
	}
}
const addSlotsOverrides = (inflateSlotsData: SlotsDataItem, overrideSlotsData: SlotsDataItem) => {
	if (!overrideSlotsData.slots) {
		return inflateSlotsData
	}
	return {
		...inflateSlotsData,
		slots: {
			...overrideSlotsData.slots,
		},
	}
}

const resolveInternalRefData = (
	masterPageData: Data,
	structure: ResolvedRef['structure'],
	queriesToCompId: Record<string, string>
): RefData =>
	_.mapValues(masterPageData, (dataMap, dataMapName) =>
		_.reduce(
			dataMap,
			(acc, item, dataItemId) => {
				if (
					dataMapName === pointers.data.DATA_MAPS.STYLE &&
					item.type === 'TopLevelStyle' &&
					(item as Style & { styleType: string }).styleType === 'system'
				) {
					return acc
				}

				const parentRefId = dataItemId.split(REF_DELIMITER)[0]
				const isRefOverrideOfInnerRef = !!parentRefId && !!structure[parentRefId]

				if (!queriesToCompId[dataItemId] && !isRefOverrideOfInnerRef) {
					return acc
				}

				acc[dataItemId] = item
				if (item.type !== 'RefArray') {
					return acc
				}

				_.forEach((item as UnresolvedRefArray).values, (value) => {
					const leafOrVariantRelation = dataMap[removeHash(value)]
					acc[leafOrVariantRelation.id] = leafOrVariantRelation

					if (leafOrVariantRelation.type === 'VariantRelation') {
						const leafItem = dataMap[removeHash((leafOrVariantRelation as UnresolvedVariantRelation).to)]
						acc[leafItem.id] = leafItem
					}
				})

				return acc
			},
			{} as typeof dataMap
		)
	) as RefData

export const resolveInternalRefs = (
	rootId: string,
	masterPageStructure: Structure,
	masterPageData: Data
): Omit<ResolvedRef, 'translations'> => {
	const { structure, queriesToCompId } = resolveInternalRefStructure(rootId, masterPageStructure, masterPageData)
	const data: RefData = resolveInternalRefData(masterPageData, structure, queriesToCompId)

	return {
		structure,
		data,
		rootId,
		originalStructure: structure,
	}
}

export const invert = (object: Record<string, string>): Record<string, string> => _.invert(object)

export {
	inflateRefArrayWithPresets,
	mergeRefArrayOverrides,
	getUniqueId,
	getUniqueDataQuery,
	mergeData,
	fixDataAfterInflate,
	REF_DELIMITER,
	buildWidgetUrl,
	getHiddenCompsGroupedByRefComponent,
	inflateTemplateStructure,
	getQueriesToCompIdMap,
	inflatedControllerConnectionsControllerId,
	inflateActionBehaviorsItemsCompIds,
	getFullId,
	getItemId,
	getDisplayedId,
	getRefCompIds,
	getQueries,
	getMultipleRefCompsIdFromInflatedId,
	getTemplateFromInflatedId,
	addSlotsOverrides,
	inflateVariables,
	inflateVariableConnections,
}
